@file:OptIn(FlowPreview::class, ExperimentalTime::class)

package com.wintermar.inspection.core.ui.util

import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.snapshotFlow
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChangedBy
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.map
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

private enum class ScrollDirectionType { Up, Down, None }

data class ScrollDirectionConfig(
    val debounceMs: Long = 50L,
    val suppressDuringGesture: Boolean = true,
    val minEmitGapMs: Long = 300L,
    val endReachedOffset: Int = 0,
    val endReachedEnabled: Boolean = true,
)

/**
 * Emits a single direction callback for rapid alternating events by enforcing a minimum
 * gap between delivered events. The first direction in a quick sequence is preferred.
 */
@Suppress("FrequentlyChangingValue")
@Composable
fun ScrollDirection(
    listState: LazyListState,
    config: ScrollDirectionConfig = ScrollDirectionConfig(),
    onScrollUp: () -> Unit = {},
    onScrollDown: () -> Unit = {},
    onEndReached: () -> Unit = {}
) {
    val debounceMs = config.debounceMs
    val suppressDuringGesture = config.suppressDuringGesture
    val minEmitGapMs = config.minEmitGapMs
    val endReachedOffset = config.endReachedOffset
    val endReachedEnabled = config.endReachedEnabled
    val onUp by rememberUpdatedState(onScrollUp)
    val onDown by rememberUpdatedState(onScrollDown)
    val onEnd by rememberUpdatedState(onEndReached)

    // Detect end of list
    val isEndReached by remember(listState) {
        derivedStateOf {
            val lastVisible = listState.layoutInfo.visibleItemsInfo.lastOrNull()
            val total = listState.layoutInfo.totalItemsCount
            lastVisible != null && total > 0 &&
                    lastVisible.index >= total - 1 - endReachedOffset
        }
    }

    LaunchedEffect(isEndReached, listState.layoutInfo.totalItemsCount) {
        if (isEndReached && endReachedEnabled) onEnd()
    }

    LaunchedEffect(listState, suppressDuringGesture, minEmitGapMs) {
        var prevIndex = listState.firstVisibleItemIndex
        var prevOffset = listState.firstVisibleItemScrollOffset
        var lastEmitTime = 0L

        snapshotFlow {
            Triple(
                listState.firstVisibleItemIndex,
                listState.firstVisibleItemScrollOffset,
                listState.isScrollInProgress
            )
        }
            .map { (index, offset, isScrolling) ->
                val dir = when {
                    index > prevIndex || offset > prevOffset -> ScrollDirectionType.Down
                    index < prevIndex || offset < prevOffset -> ScrollDirectionType.Up
                    else -> ScrollDirectionType.None
                }
                prevIndex = index
                prevOffset = offset
                Pair(dir, isScrolling)
            }
            .filter { (dir, _) -> dir != ScrollDirectionType.None }
            .distinctUntilChangedBy { (dir, _) -> dir }
            .debounce(debounceMs)
            .collect { (dir, _) ->
                val now = Clock.System.now().toEpochMilliseconds()
                val allowedByGap = now - lastEmitTime >= minEmitGapMs

                if (!allowedByGap) {
                    return@collect
                }

                lastEmitTime = now
                when (dir) {
                    ScrollDirectionType.Up -> onUp()
                    ScrollDirectionType.Down -> onDown()
                    else -> {
                        // no-op
                    }
                }
            }
    }
}
